// src/components/pages/Profile.jsx
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../context/AuthContext';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { AuditLoggers } from '../../utils/auditLogger';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateSelectMenuProps,
  harmonateToolbarBuFieldSx,
} from '../../theme/harmonateStyles';
import { isHarmonateUser as isServerHarmonateUser } from '../../auth/serverIdentity';

const fieldLabelSx = {
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: 0.4,
  color: ht.text,
  mb: 0.75,
  textTransform: 'uppercase',
};

const fieldValueSx = {
  color: ht.text,
  fontWeight: 500,
  wordBreak: 'break-word',
};

const MASKED_ID = '••••••••';

const MaskedIdField = ({ label, value, emptyLabel = 'Not available' }) => {
  const [revealed, setRevealed] = useState(false);
  const hasValue = Boolean(value);

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={fieldLabelSx}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            ...fieldValueSx,
            flex: 1,
            minWidth: 0,
            letterSpacing: revealed || !hasValue ? undefined : 1.5,
            fontFamily: revealed && hasValue ? 'monospace' : 'inherit',
          }}
        >
          {hasValue ? (revealed ? value : MASKED_ID) : emptyLabel}
        </Typography>
        {hasValue ? (
          <Tooltip title={revealed ? 'Hide' : 'Reveal'}>
            <IconButton
              size="small"
              aria-label={revealed ? `Hide ${label}` : `Reveal ${label}`}
              onClick={() => setRevealed((prev) => !prev)}
              sx={{ color: ht.text, flexShrink: 0 }}
            >
              {revealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

const Profile = () => {
  const { user } = useAuth();

  // State for current user
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserLoading, setCurrentUserLoading] = useState(true);
  const [currentUserError, setCurrentUserError] = useState(null);

  // State for tenants
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(localStorage.getItem('tenant_id') || '');
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [tenantsError, setTenantsError] = useState(null);
  
  // State for selected tenant info from localStorage
  const [selectedTenantId, setSelectedTenantId] = useState(localStorage.getItem('tenant_id') || '');
  const [selectedTenantName, setSelectedTenantName] = useState(localStorage.getItem('tenant_name') || '');

  // Fetch current user info on mount
  useEffect(() => {
    const fetchCurrentUser = async () => {
      setCurrentUserLoading(true);
      setCurrentUserError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/get-current-user`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Failed to fetch current user');
        const data = await res.json();
        setCurrentUser(data);
      } catch (err) {
        setCurrentUserError(err.message);
      } finally {
        setCurrentUserLoading(false);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch tenants for both harmonate and normal users
  useEffect(() => {
    const isHarmonateUser = isServerHarmonateUser();
    const userId = localStorage.getItem('user_id');
    
    setTenantsLoading(true);
    setTenantsError(null);
    
    const fetchTenants = async () => {
      try {
        const token = localStorage.getItem('token');
        let apiUrl;
        
        // Use different API based on user type
        if (isHarmonateUser) {
          // Harmonate users: Get all tenants
          apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/tenants`;
        } else {
          // Normal users: Get only their associated tenants
          if (!userId) {
            throw new Error('User ID not found');
          }
          apiUrl = `${process.env.REACT_APP_BACKEND_URL}conductor/users/${userId}/tenants`;
        }
        
        const res = await fetch(apiUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!res.ok) throw new Error('Failed to fetch tenants');
        const data = await res.json();
        setTenants(data);
      } catch (err) {
        setTenantsError(err.message);
        console.error('Error fetching tenants:', err);
      } finally {
        setTenantsLoading(false);
      }
    };
    
    fetchTenants();
  }, []);


  // Handle tenant change
  const handleTenantChange = async (event) => {
    const newTenantId = event.target.value;
    const oldTenantId = selectedTenant;
    
    try {
      // Find the selected tenant to get its name
      const selectedTenantData = tenants.find(tenant => tenant.id === newTenantId);
      const tenantName = selectedTenantData?.name || 'Unknown';
      
      // Find the previous tenant name
      const previousTenantData = tenants.find(tenant => tenant.id === oldTenantId);
      const previousTenantName = previousTenantData?.name || 'Previous Tenant';
      
      // Log the tenant switch using the audit logger
      await AuditLoggers.tenantSwitched(
        oldTenantId,
        newTenantId,
        tenantName,
        {
          userId: user?.id || localStorage.getItem('user_id'),
          username: user?.username || localStorage.getItem('username'),
          previousTenantName: previousTenantName
        }
      );
      
      // Update the selected tenant
      setSelectedTenant(newTenantId);
      localStorage.setItem('tenant_id', newTenantId);
      localStorage.setItem('tenant_name', tenantName);
      
      // Reload the page to apply the new tenant context
      window.location.reload();
    } catch (error) {
      console.error('Error switching tenant:', error);
      // Optionally show an error message to the user
    }
  };

  // Update tenant_integration based on selected tenant
  useEffect(() => {
    const updateTenantIntegration = () => {
      try {
        const currentTenantId = localStorage.getItem('tenant_id');
        
        if (currentTenantId && tenants.length > 0) {
          // Find the selected tenant from the tenants list
          const selectedTenantData = tenants.find(tenant => tenant.id === currentTenantId);
          
          if (selectedTenantData && selectedTenantData.database) {
            localStorage.setItem('tenant_integration', selectedTenantData.database);
            console.log('Updated tenant_integration:', selectedTenantData.database);
            console.log('Selected tenant:', selectedTenantData.name);
          } else {
            console.warn('Selected tenant not found or missing database field');
          }
          
          if (selectedTenantData && selectedTenantData.name) {
            localStorage.setItem('tenant_name', selectedTenantData.name);
            console.log('Updated tenant_name:', selectedTenantData.name);
          }
        }
      } catch (err) {
        console.error('Error updating tenant integration:', err);
      }
    };
    
    updateTenantIntegration();
  }, [selectedTenant, tenants]); // Re-run when selectedTenant or tenants list changes

  // Update selected tenant info from localStorage
  useEffect(() => {
    const updateSelectedTenantInfo = () => {
      setSelectedTenantId(localStorage.getItem('tenant_id') || '');
      setSelectedTenantName(localStorage.getItem('tenant_name') || '');
    };
    
    // Update on mount and when selectedTenant changes
    updateSelectedTenantInfo();
  }, [selectedTenant]);

  // Also update on mount to ensure values are current
  useEffect(() => {
    setSelectedTenantId(localStorage.getItem('tenant_id') || '');
    setSelectedTenantName(localStorage.getItem('tenant_name') || '');
  }, []);

  return (
    <Box sx={{ ...harmonatePageSx, p: 3, overflowY: 'auto' }}>
      {/* Header row: Profile title left, tenant dropdown right */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ ...harmonatePageTitleSx, mb: 0, fontSize: '1.5rem' }}>
          Profile
        </Typography>
        {/* Tenant dropdown - visible for all users */}
        {tenants.length > 0 && (
          <FormControl
            size="small"
            sx={{ ...harmonateToolbarBuFieldSx, minWidth: { xs: '100%', sm: 280 }, width: { xs: '100%', sm: 280 } }}
          >
            <InputLabel id="tenant-select-label">Select Tenant</InputLabel>
            <Select
              labelId="tenant-select-label"
              value={selectedTenant}
              label="Select Tenant"
              onChange={handleTenantChange}
              disabled={tenantsLoading}
              MenuProps={harmonateSelectMenuProps}
            >
              {tenantsLoading && <MenuItem value=""><em>Loading...</em></MenuItem>}
              {tenantsError && <MenuItem value=""><em>Error loading tenants</em></MenuItem>}
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>{tenant.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>
      
      {/* Current User Info */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 4, height: '100%', bgcolor: ht.white }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: ht.text }}>
              User Information
            </Typography>
            {currentUserLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} sx={{ color: ht.blue }} />
                <Typography sx={{ color: ht.text }}>Loading current user...</Typography>
              </Box>
            ) : currentUserError ? (
              <Alert severity="error">{currentUserError}</Alert>
            ) : currentUser ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3.5 }}>
                  <Avatar
                    sx={{
                      width: 100,
                      height: 100,
                      mr: 3,
                      bgcolor: ht.line,
                      color: ht.white,
                    }}
                    alt={currentUser.username || 'User'}
                  >
                    <PersonIcon sx={{ fontSize: 56 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: ht.text }}>
                      {currentUser.username || 'User'}
                    </Typography>
                    <Typography variant="body1" sx={{ color: ht.text }}>
                      {currentUser.email || 'No email provided'}
                    </Typography>
                  </Box>
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={fieldLabelSx}>
                        FULL NAME
                      </Typography>
                      <Typography variant="body1" sx={fieldValueSx}>
                        {`${currentUser.firstName || currentUser.first_name || ''} ${currentUser.lastName || currentUser.last_name || ''}`.trim() || 'Not provided'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={fieldLabelSx}>
                        USERNAME
                      </Typography>
                      <Typography variant="body1" sx={fieldValueSx}>
                        {currentUser.username || 'Not provided'}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" sx={fieldLabelSx}>
                        EMAIL
                      </Typography>
                      <Typography variant="body1" sx={fieldValueSx}>
                        {currentUser.email || 'Not provided'}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            ) : null}
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ ...harmonateCardSx, p: 4, height: '100%', bgcolor: ht.white }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: ht.text }}>
              Account Details
            </Typography>
            {currentUser && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <MaskedIdField
                    label="TENANT ID"
                    value={selectedTenantId || currentUser.tenant_id}
                    emptyLabel="Not assigned"
                  />
                </Grid>
                <Grid item xs={12}>
                  <MaskedIdField
                    label="USER ID"
                    value={currentUser.id || currentUser.user_id}
                    emptyLabel="Not available"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={fieldLabelSx}>
                      ACCOUNT STATUS
                    </Typography>
                    <Typography variant="body1" sx={{ color: ht.green, fontWeight: 600 }}>
                      Active
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ mb: 2.5 }}>
                    <Typography variant="body2" sx={fieldLabelSx}>
                      TENANT NAME
                    </Typography>
                    <Typography variant="body1" sx={fieldValueSx}>
                      {selectedTenantName ||
                        currentUser.tenant_details?.name ||
                        'Not available'}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
